import { Promise } from 'es6-promise';
import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { AddButton } from "../Buttons/AddButton";
import { ButtonGroup } from "../Buttons/ButtonGroup";
import { CloseButton } from "../Buttons/CloseButton";
import { DeleteButton } from "../Buttons/DeleteButton";
import { MoveButton } from "../Buttons/MoveButton";
import { SaveButton } from "../Buttons/SaveButton";
import { SelectionButton } from "../Buttons/SelectionButton";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import * as DocumentManagement from "../DocumentManagement";
import { AreaBody } from "../General/AreaBody";
import { ContentGroup } from "../General/ContentGroup";
import { ShortCutKeyHandler } from "../General/ShortCutKeyHandler";
import { Toolbar } from "../General/Toolbar";
import { WizardBox, WizardBoxType } from "../General/WizardBox";
import * as UiStore from "../States/AreaUiInfo";
import { AppStore } from "../Stores/AppStore";
import { CollectionStoreImpl } from "../Stores/CollectionStore";
import { DialogResult, DialogStore } from "../Stores/DialogStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { Strings } from "../Stores/StringsStore";
import { Urls } from "../Urls";
import * as Utilities from "../Utilities";
import { newGuid } from "../Utilities";
import { CollectionDocumentsEdit } from "./CollectionDocumentsEdit";
import { CollectionPrivilegesEdit } from "./CollectionPrivilegesEdit";
import { CollectionTree } from "./CollectionTree";
import { PrivilegesSelection } from "./PrivilegesSelection";



export interface ICollectionProps extends RouteComponentProps<ICollectionPropsParams>, UiStore.IAreaUiInfo {
    store?: CollectionStoreImpl;
}

interface ICollectionPropsParams {
    parrentId?: string;
    id: string;
}

@inject('store')
@inject('areaUiInfo')
@observer
export class CollectionEdit extends React.Component<ICollectionProps, {}> {

    store: CollectionStoreImpl;
    @observable collectionHash: string;
    isNewDocumentSave: boolean;

    componentWillMount() {
        this.store = this.props.store;
        AppStore.isBusyModelLoading = true;
        this.store.collectionPrivilegesWizardState.onConfirmExternal = this.onAddPrivileges;
    }

    componentDidMount() {
        this.getCollection();
        this.setTitle();
    }

    componentDidUpdate(prevProps: ICollectionProps) {
        if (this.props.match.params.id != prevProps.match.params.id && !prevProps.match.params.parrentId) {
            this.props.store.addedPrivileges = [];
            this.store.removedCollectionPrivileges = [];
            this.getCollection();
        }
    }

    componentWillUnmount() {
        this.store.collection = null;
    }

    setTitle = () => {
        if (this.store.collection && this.store.collection.isNew) {
            AppStore.setTitle(Strings.documentManagement.collectionEdit.addTitle);
        }
        else {
            AppStore.setTitle(Strings.documentManagement.collectionEdit.title);
        }
    }

    @computed get canSave(): boolean {
        /**
        * Checks collection object
        * Checks If any privilege memberships in selected privileges checked or not. Save button can only be enable if there is a change in privilege membership in selected privilege not only by selecting a privilege
        */
        let canSave = this.store.collection && this.store.collection.name.value.trim() && this.store.collection.name.isValid && ((this.collectionHash != JSON.stringify(this.store.collection)) || (this.props.store.addedPrivileges.some(privilege => privilege.privilegeMemberships.some(item => item.isExplicitMemberOf == true || item.isImplicitMemberOf == true) == true)));
        AppStore.isDirtyMode = canSave;
        AppStore.isDirtyChange = ((this.collectionHash != JSON.stringify(this.store.collection)) || (this.props.store.addedPrivileges.length > 0));
        AppStore.saveOnLeave = this.saveOnDirtyLeave;
        return canSave
    }

    @computed get canAddDocument(): boolean {
        let canAddDocument: boolean = this.store.collection != null ? this.store.collection.name != null : false;
        if (this.store.collection != null && this.store.collection.name != null && this.store.collection.isNew) {
            {
                return this.store.collection.name.value != null && this.isNewDocumentSave;
            }
        }
        return canAddDocument;
    }

    @action setCollection(model: DocumentManagement.CollectionDetails) {
        this.store.collection = model;
        this.collectionHash = JSON.stringify(this.store.collection);
        this.props.store.currentCollections = this.store.collection.guid;
        this.store.tempStorageCollectionPrivileges = this.store.collection.privileges;
        NotificationStore.clearMessage();
        AppStore.isBusyModelLoading = false;
        this.setTitle();
    }

    @action getCollection() {
        AppStore.isBusyModelLoading = true;
        let collectionPromise: Promise<DocumentManagement.CollectionDetails>;
        if (this.props.match.params.parrentId) {
            this.store.asideTreeViewStore.currentFocusedCollection = this.props.match.params.parrentId;
            collectionPromise = DocumentManagement.getNewChildCollectionAsync(this.props.match.params.parrentId)
        }
        else {
            this.store.asideTreeViewStore.currentFocusedCollection = this.props.match.params.id;
            collectionPromise = DocumentManagement.getCollectionAsync(this.props.match.params.id);
        }
        collectionPromise.then(result => {
            this.setCollection(result);
        }).catch(error => {
            DialogStore.showError(Strings.general.errorDialogTitle, error);
        });
    }

    handleSaveUi = () => {
        this.saveModel();
    }

    saveOnDirtyLeave = (onSaveCompleted: () => void) => {
        this.saveModel(onSaveCompleted);
    }

    private saveModel(onSaveCompleted?: () => void) {
        if (this.canSave) {
            AppStore.isBusy = true;
            // Adding Selected privileges and existign privileges to one object for update the data.
            let selectedprivileges = new DocumentManagement.CollectionPrivileges();
            selectedprivileges.collectionId = this.store.collection.privileges.collectionId;
            this.props.store.addedPrivileges.map((privilege) => { selectedprivileges.privilegesForGroups.push(privilege) });
            this.store.collection.privileges.privilegesForGroups.map((privilege) => { selectedprivileges.privilegesForGroups.push(privilege) });

            let update = new DocumentManagement.CollectionDetailsUpdate();
            update.lastModified = this.store.collection.lastModified;
            update.guid = this.store.collection.guid;
            update.parentId = this.store.collection.parentId;
            update.name = this.store.collection.name.value;
            update.privileges = selectedprivileges;
            DocumentManagement.insertOrUpdateCollectionAsync(update).then(collection => {
                this.store.collection = collection;
                this.collectionHash = JSON.stringify(this.store.collection);
                this.props.store.addedPrivileges = [];
                this.store.tempStorageCollectionPrivileges = this.store.collection.privileges;
                this.store.removedCollectionPrivileges = [];
                AppStore.isBusy = false;
                NotificationStore.showSuccessMessage(Strings.general.saveSuccessMessage);
                if (this.props.match.params.parrentId !== undefined) {
                    AppStore.history.replace(`/collections/${this.store.collection.guid}`);
                }
                this.isNewDocumentSave = true;
                this.store.asideTreeViewStore.getRoots();
                if (onSaveCompleted) {
                    onSaveCompleted();
                }
            }).catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));

        }
    }

    handleDelete = () => {
        DialogStore.show(Utilities.format(Strings.general.dialogDeleteTitle, this.store.collection.name.value), Utilities.format(Strings.general.dialogDeleteText, this.store.collection.name.value)).then(result => {
            if (result == DialogResult.Yes) {
                AppStore.isBusy = true;
                DocumentManagement.removeCollectionAsync({ guid: this.store.collection.guid, lastModified: this.store.collection.lastModified }).then(status => {
                    AppStore.isBusy = false;
                    NotificationStore.showSuccessMessage(Strings.general.deleteSuccessMessage);
                    this.props.store.currentCollections = "";
                    this.store.asideTreeViewStore.getRoots();
                    AppStore.history.replace("/collections");
                }).catch(error => {
                    AppStore.isBusy = false;
                    DialogStore.showError(Strings.general.errorDialogTitle, error);
                });
            }
        });
    }

    handleCancel = () => {
        AppStore.keepOnPage("/collections", null, this.store.areaUiInfo.closeExpand);
        this.props.store.currentCollections = "";
        this.props.store.asideTreeViewStore.currentFocusedCollection = null;
    }

    handleAddSubCollection = () => {
        AppStore.keepOnPage(`${Urls.collections}/${this.store.collection.guid}/${newGuid()}`, true);
    }

    handleAddDocument = () => {
        AppStore.keepOnPage(`${Urls.collections}/${this.store.collection.guid}/documents/${newGuid()}`, true, null, true);
    }

    //starts the wizard for adding collections privileges.
    handleAddCollectionPrivilege = () => {
        this.props.store.collectionPrivilegesWizardState.open();
    }

    @action handleNameChange = (event: React.FormEvent<any>) => {
        this.store.collection.name.value = (event.target as any).value as string;
        this.store.collection.name.isValid = this.store.collection.name.value != "";
        NotificationStore.clearMessage();
    }

    @action handleMoveCurrent = () => {
        let collectionInfo = new DocumentManagement.CollectionInfo();
        collectionInfo.guid = this.store.collection.guid;
        collectionInfo.parentId = this.store.collection.parentId;
        this.props.store.asideTreeViewStore.addCollectionToSelection(collectionInfo);
        this.props.store.collectionMoveWizardState.open();
    }



    @action handleDocumentSelectionMode = () => {
        this.props.store.toggleDocumentSelectionMode();
    }

    @action handlePrivilegeSelectionMode = () => {
        this.props.store.togglePrivilegeSelectionMode();
    }

    shortcuts: { [key: string]: any } = {
        27: this.handleCancel
    }

    refs: {
        [key: string]: ContentGroup;
        privilegeContentGroup: ContentGroup
        documentContentGroup: ContentGroup;
    }

    onAddPrivileges = () => {
        this.refs.privilegeContentGroup.collapsed = false;
    }

    @computed get areaAsideButtonTooltip() {
        return this.props.areaUiInfo.isCollapsed ? Strings.documentManagement.collectionEdit.areaAsideButtonCollapsedTooltip : Strings.documentManagement.collectionEdit.areaAsideButtonExpandedTooltip;
    }

    render() {
        //Components CollectionPrivileges & CollectionDocumentsEdit depends on the collection object. The output will be rendered after the collection has been loaded successfully.
        return <AreaBody loading={AppStore.isBusyModelLoading}>
            <ShortCutKeyHandler shortcuts={this.shortcuts} />
            <Toolbar>
                <ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
                <ButtonGroup asideWidth={this.props.areaUiInfo.asideWidth} isReactiveMenu={true}>
                    <SaveButton id="btnSave" prioritization={2} onClick={this.handleSaveUi} disabled={!this.canSave} testId="saveButton" />
                    <DeleteButton id="btnDelete" onClick={this.handleDelete} disabled={!this.store.collection || this.store.collection.isNew} testId="deleteButton" />
                    <MoveButton title={Strings.documentManagement.collectionEdit.moveButtonTooltip} id="btnMove" prioritization={3} onClick={this.handleMoveCurrent} testId="moveButton" />
                </ButtonGroup>

                <div className="buttongroup is--buttongroup-right">
                    <CloseButton onClick={this.handleCancel} testId="closeButton" />
                </div>
            </Toolbar>

            <div className="area-grid__body-content">

                <div className="content-main">

                    <h1>{!this.store.collection || this.store.collection.isNew ? Strings.documentManagement.collectionEdit.addTitle : Strings.documentManagement.collectionEdit.title}</h1>
                    <div>
                        <label className="required">
                            <span>{Strings.documentManagement.collectionEdit.nameLabel}</span>
                            <input type="text" placeholder={Strings.documentManagement.collectionEdit.namePlaceholder} value={this.store.collection && this.store.collection.name.value} onChange={this.handleNameChange} className={this.store.collection && !this.store.collection.name.isValid ? "is--input-invalid" : ""} data-test-id="nameInput" />
                        </label>
                    </div>

                    <ContentGroup ref="privilegeContentGroup" caption={Strings.documentManagement.collections.privilegesCaption} uiContext={this.props.areaUiInfo} >
                        <ButtonGroup>
                            <AddButton title={Strings.documentManagement.documentEdit.privilegesAddButtonTooltip} onClick={this.handleAddCollectionPrivilege} testId="privilegeAddButton" />
                            <CloseButton title={Strings.documentManagement.documentEdit.privilegesDeleteButtonTooltip} hide={!this.props.store.privilegeSelectionMode} disabled={!this.store.hasSelectedPrivilegeItems} onClick={this.props.store.handlePrivilegesDelete} testId="privilegeDeleteButton" />
                            <SelectionButton title={Strings.documentManagement.documentEdit.privilegesSelectionButtonTooltip} onClick={this.handlePrivilegeSelectionMode} pressed={this.props.store.privilegeSelectionMode} testId="privilegeSelectionButton" />
                            <span className="tinytool__splitter"></span>
                        </ButtonGroup>
                        {
                            !AppStore.isBusyModelLoading ?
                                <CollectionPrivilegesEdit key={"priveleg-" + this.store.collection.guid} collection={this.store.collection} />
                                : null
                        }
                    </ContentGroup>
                    {
                        this.props.store.collectionPrivilegesWizardState.isOpen ? <WizardBox context={this.props.store.collectionPrivilegesWizardState} type={WizardBoxType.ConfirmReject}>
                            <PrivilegesSelection collectionID={this.props.match.params.id} store={this.props.store} />
                        </WizardBox> : null
                    }

                    <ContentGroup caption={Strings.documentManagement.collections.documentCaption} uiContext={this.props.areaUiInfo} >
                        <ButtonGroup>
                            <DeleteButton title={Strings.documentManagement.collectionEdit.documentsDeleteButtonTooltip} hide={!this.props.store.documentSelectionMode} disabled={!this.store.hasSelectedDocumentsItems} onClick={this.props.store.handleDocumentsDelete} testId="documentDeleteButton" />
                            <MoveButton title={Strings.documentManagement.collectionEdit.documentsMoveButtonTooltip} hide={!this.props.store.documentSelectionMode} disabled={!this.store.hasSelectedDocumentsItems} onClick={this.props.store.documentMoveWizardState.open} testId="documentMoveButton" />
                            <AddButton title={Strings.documentManagement.collectionEdit.documentsAddButtonTooltip} hide={this.props.store.documentSelectionMode} onClick={this.handleAddDocument} disabled={!this.canAddDocument} testId="documentAddButton" />
                            <SelectionButton title={Strings.documentManagement.collectionEdit.documentsSelectionButtonTooltip} onClick={this.handleDocumentSelectionMode} pressed={this.props.store.documentSelectionMode} testId="documentSelectionButton" />
                            <span className="tinytool__splitter"></span>
                        </ButtonGroup>
                        <CollectionDocumentsEdit key={"document-" + (this.store.collection && this.store.collection.guid)} collection={this.store.collection} testId="collectionDocuments" isDocumentGroupSaved={!this.canAddDocument} />

                    </ContentGroup>
                    {
                        this.props.store.documentMoveWizardState.isOpen ? <WizardBox context={this.props.store.documentMoveWizardState} type={WizardBoxType.ConfirmReject}>
                            <div className="wizardbox-content no--toolbar">
                                <div className="wizardbox-module">
                                    <CollectionTree treeViewStore={this.props.store.moveTreeViewStoreDocuments} />
                                </div>
                            </div>
                        </WizardBox> : null
                    }

                </div>

            </div>

        </AreaBody>;

    }
}
